import React from 'react';
import { GitBranch, ArrowRight, Circle } from 'lucide-react';

const DependenciesGraph = ({ dependencies, projects }) => {
  if (!dependencies || dependencies.length === 0 || !projects || projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-[var(--clr-text-muted)]">
        <GitBranch className="w-12 h-12 mb-3" />
        <p className="text-sm">No dependencies to visualize</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const statusColors = {
      'Blocked': '#8E44AD',
      'Behind': '#E74C3C',
      'Due Date at Risk': '#F1C40F',
      'In Progress': '#3498DB',
      'Completed': '#2ECC71',
      'In Review': '#16A085',
      'Not Started': '#BDC3C7',
      'Not Required': '#95A5A6'
    };
    return statusColors[status] || '#95A5A6';
  };

  const getDepTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'blocks':
      case 'blocking':
        return 'border-red-400 bg-[var(--clr-sev-blocked)] bg-opacity-10';
      case 'depends':
      case 'dependency':
        return 'border-yellow-400 bg-[var(--clr-sev-atrisk)] bg-opacity-10';
      default:
        return 'border-blue-400 bg-[var(--clr-brand-primary)] bg-opacity-10';
    }
  };

  const getDepTypeTextColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'blocks':
      case 'blocking':
        return 'text-[var(--clr-sev-blocked)]';
      case 'depends':
      case 'dependency':
        return 'text-[var(--clr-sev-atrisk)]';
      default:
        return 'text-[var(--clr-brand-primary)]';
    }
  };

  // Build a map of project names to project data
  const projectById = {};
  projects.forEach(p => {
    projectById[p.id] = p;
  });

  return (
    <div className="w-full" data-testid="dependencies-graph">
      {/* Dependency Cards */}
      <div className="space-y-4">
        {dependencies.map((dep) => {
          const fromProject = projectById[dep.from_project_id];
          const toProject = projectById[dep.to_project_id];
          
          if (!fromProject || !toProject) return null;

          return (
            <div 
              key={dep.id}
              className={`p-4 rounded-lg border-2 ${getDepTypeColor(dep.dep_type)}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs font-semibold uppercase tracking-wide ${getDepTypeTextColor(dep.dep_type)}`}>
                  {dep.dep_type || 'relates'}
                </span>
                {dep.aging !== undefined && (
                  <span className="text-xs text-[var(--clr-text-muted)]">
                    {dep.aging} days old
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                {/* From Project */}
                <div className="flex-1 flex items-center gap-3 p-3 bg-[var(--clr-surface-card)] rounded-lg shadow-[var(--shadow-card)]">
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getStatusColor(fromProject.status) }}
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--clr-text-primary)] truncate">{fromProject.name}</p>
                    <p className="text-xs text-[var(--clr-text-muted)]">{fromProject.status}</p>
                  </div>
                </div>
                
                {/* Arrow */}
                <div className="flex-shrink-0">
                  <ArrowRight className={`w-6 h-6 ${getDepTypeTextColor(dep.dep_type)}`} />
                </div>
                
                {/* To Project */}
                <div className="flex-1 flex items-center gap-3 p-3 bg-[var(--clr-surface-card)] rounded-lg shadow-[var(--shadow-card)]">
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getStatusColor(toProject.status) }}
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--clr-text-primary)] truncate">{toProject.name}</p>
                    <p className="text-xs text-[var(--clr-text-muted)]">{toProject.status}</p>
                  </div>
                </div>
              </div>
              
              {dep.description && (
                <p className="mt-3 text-sm text-[var(--clr-text-secondary)] italic">
                  "{dep.description}"
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-6 mt-6 px-4 py-3 bg-[var(--clr-surface-subtle)] rounded-lg text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-[var(--clr-sev-blocked)]"></div>
          <span className="text-[var(--clr-text-secondary)]">Blocks</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-[var(--clr-sev-atrisk)]"></div>
          <span className="text-[var(--clr-text-secondary)]">Depends</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-[var(--clr-brand-primary)] bg-opacity-100"></div>
          <span className="text-[var(--clr-text-secondary)]">Related</span>
        </div>
        <div className="flex items-center gap-2 border-l border-gray-300 pl-6">
          <ArrowRight className="w-3 h-3 text-[var(--clr-text-muted)]" />
          <span className="text-[var(--clr-text-secondary)]">Direction of dependency</span>
        </div>
      </div>
    </div>
  );
};

export default DependenciesGraph;
